import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddressSuggestions, SecureDataService } from '../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { Static_States, StateDefinition } from '../../../assets/static-data/static-data';
import { DataFieldDirectiveService } from '../../common/directives/date-field.directive';
import { EmailVerificationComponent, EmailVerifyStatus } from '../../common/modules/email-verification/email-verification.component';
import { PhoneVerificationComponent, PhoneVerifyStatus } from '../../common/modules/phone-verification/phone-verification.component';
import { LanguageStateService } from '../../common/services/language-state.service';
import { AddressSuggestion, Suggestion } from '../customer-information-collection/customer-information-collection.component';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Button } from 'primeng/button';
import { InputMask } from 'primeng/inputmask';
import { creditLabelKey } from '../../common/utils/credit-label';
import { EMAIL_PATTERN } from '../../common/constants/validation-patterns';

export type IdType = 'Driver’s License' | 'State Issued ID' | 'U.S. Military ID' | 'U.S. Passport' | 'Foreign Government ID';
const GENERIC_SSN = '111001111'

@Component({
    selector: 'app-credit-information',
    templateUrl: './credit-information.component.html',
    styleUrl: './credit-information.component.scss',
    standalone: false
})
export class CreditInformationComponent extends BaseViewComponent {
  fb: FormBuilder = inject(FormBuilder)
  #secureDataService: SecureDataService = inject(SecureDataService)
  #dateService: DataFieldDirectiveService = inject(DataFieldDirectiveService)
  languageService: LanguageStateService = inject(LanguageStateService)
  states = this.#secureDataService.States;
  countries = this.#secureDataService.Countries;
  isConsentAgreed = false;
  isSMB = false;
  showWaiting = false;
  serviceAddress: string = '';
  creditInformationForm: FormGroup | undefined;
  filteredAddresses: AddressSuggestion[] = [];
  addresses: AddressSuggestions | undefined;
  selectedAddress: string | undefined;
  showFullAddress = false;
  idTypes = ['Driver’s License', 'State Issued ID', 'U.S. Military ID', 'U.S. Passport', 'Foreign Government ID'];
  selectedIdType: IdType | undefined;
  showCancelDialog = false;
  currentStateTooltip = '';
  selectedState: StateDefinition | undefined;
  legalDescription: string = '';
  displayStructureType = false;
  displayFloor = false;
  isEmailVerifiedByCustomer = signal<boolean>(false);
  isPhoneVerifiedByCustomer = signal<boolean>(false);
  creditChecked = signal<boolean>(false);
  // The customer already has SSN and DOB stored from an earlier credit check, so they are not asked again (bug 11481).
  creditOnFile = signal<boolean>(false);
  creditSetup = signal<any>(undefined);
  creditInformationState = signal<any>(undefined);
  authUserList = signal<string[]>([]);
  @ViewChild('showFullAddressBtn', { static: false }) showFullAddressBtn!: Button;
  @ViewChild('autoComplete') autoComplete?: AutoComplete;
  @ViewChild(PhoneVerificationComponent) phoneVerificationComponent: PhoneVerificationComponent
  @ViewChild(EmailVerificationComponent) emailVerificationComponent: EmailVerificationComponent
  @ViewChild('zipCodeInput') zipCodeInput: InputMask

  unitTypes = [
    { label: '-', value: null },
    { label: 'Unit', value: 'Unit' },
    { label: 'Suite', value: 'Ste' },
    { label: 'Apartment', value: 'Apt' },
    { label: 'Room', value: 'Room' },
    { label: 'Slip', value: 'Slip' },
    { label: 'Lot', value: 'Lot' }
  ]
  structureTypes = ['Building', 'Pier', 'Wing', 'Trailer']

  constructor() {
    super()
    effect(() => {
      this.initConditionalValidators()
    })
  }
  // Note: Do not remove the below code because of testing purposes

  // creditSetup = computed(() => {
  //   // return this.orderRequirements()?.Internet.CreditSetup
  //   return {
  //     Dob: 'none',
  //     EmailValidation: 'optional',
  //     PhoneValidation: 'optional',
  //     FullSSN: 'optional',
  //     Last4SSN: 'optional',
  //     PhotoId: 'required'
  //   }
  // })

  // Page title, credit section header and alternate address title from the PD/UO CreditLabels node
  secureDataPageTitle = computed(() => creditLabelKey(this.creditSetup()?.CreditLabels?.SecureDataPageTitle, 'CREDIT_INFORMATION'))
  secureDataCreditSectionName = computed(() => creditLabelKey(this.creditSetup()?.CreditLabels?.SecureDataCreditSectionName, 'CREDIT_INFORMATION'))
  alternateAddressTitle = computed(() => creditLabelKey(this.creditSetup()?.CreditLabels?.AlternateAddressTitle, 'BILLING_ADDRESS'))

  dobRequired = computed(() => {
    return this.creditSetup()?.Dob === 'required'
  })

  dobNone = computed(() => {
    return this.creditSetup()?.Dob === 'none'
  })

  emailValidationRequired = computed(() => {
    return this.creditSetup()?.EmailValidation === 'required'
  })

  phoneValidationRequired = computed(() => {
    return this.creditSetup()?.PhoneValidation === 'required'
  })

  allowBillingAddressChange = computed(() => {
    return this.creditSetup()?.AllowBillingAddressChange === true
  })

  creditCheckRequired = computed(() => {
    return this.creditSetup()?.CreditCheckRequired === true
  })

  // full ssn start
  fullSsnRequired = computed(() => {
    return this.creditSetup()?.FullSSN === 'required'
  })

  fullSsnConditional = computed(() => {
    return this.creditSetup()?.FullSSN === 'conditional'
  })

  fullSsnOptional = computed(() => {
    return this.creditSetup()?.FullSSN === 'optional'
  })

  fullSsnVisible = computed(() => {
    return this.fullSsnRequired() || this.fullSsnConditional() || (this.fullSsnOptional() && !this.last4SsnConditional())
  })
  // full ssn end

  // last 4 ssn start
  last4SsnRequired = computed(() => {
    return this.creditSetup()?.Last4SSN === 'required'
  })

  last4SsnConditional = computed(() => {
    return this.creditSetup()?.Last4SSN === 'conditional'
  })

  last4SsnVisible = computed(() => {
    return this.last4SsnRequired() || (this.last4SsnConditional() && !this.fullSsnConditional())
  })
  // last 4 ssn end

  conditionalCheckBoxVisible = computed(() => {
    return (this.fullSsnConditional() || this.last4SsnConditional()) && this.photoIdConditional()
  })

  passcodeRequired = computed(() => {
    return this.creditSetup()?.Passcode === 'required'
  })

  photoIdRequired = computed(() => {
    return this.creditSetup()?.PhotoId === 'required'
  })

  photoIdConditional = computed(() => {
    return this.creditSetup()?.PhotoId === 'conditional'
  })

  photoIdOptional = computed(() => {
    return this.creditSetup()?.PhotoId === 'optional'
  })

  photoIdVisible = computed(() => {
    return this.photoIdRequired() || this.photoIdOptional()
  })

  showPhotoIdField = computed(() => {
    const creditCheckState = this.creditInformationState()?.customerInformationCreditCheck;
    if (creditCheckState && (creditCheckState?.idType != null && creditCheckState?.idType !== 'none') && creditCheckState?.idNumber
      && creditCheckState?.issuingAuthority && creditCheckState?.expirationMonth && creditCheckState?.expirationDay && creditCheckState?.expirationYear) {
      return false;
    }
    return true
  })

  showSSNField = computed(() => {
    if (this.creditOnFile()) {
      return false;
    }
    const creditCheckState = this.creditInformationState()?.customerInformationCreditCheck;
    if (creditCheckState) {
      if (creditCheckState?.rawSsn?.length === 9) {
        return false;
      }

      if (creditCheckState?.last4Ssn?.length >= 4) {
        return false;
      }
    }
    return true;
  })

  showDobField = computed(() => {
    if (this.creditOnFile()) {
      return false;
    }
    const creditCheckState = this.creditInformationState()?.customerInformationCreditCheck;
    if (creditCheckState) {
      if (this.dobRequired() && creditCheckState.dobDay && creditCheckState.dobMonth && creditCheckState.dobYear) {
        return false;
      }
    }
    return true;
  })

  showBillingAddressField = computed(() => {
    const billingAddress = this.creditInformationState()?.billingAddress;
    return !billingAddress?.serviceAddress;
  })

  showCreditInformationSection = computed(() => {
    return (!this.dobNone() && this.showDobField())
      || (this.fullSsnVisible() && this.showSSNField())
      || (this.photoIdVisible() && this.showPhotoIdField())
      || (this.conditionalCheckBoxVisible() && this.showSSNField());
  })

  protected override refreshView(): void {
    this.creditInformationForm = this.fb.group({
      customerInfoGroup: this.fb.group({
        firstName: ['', [Validators.required, Validators.pattern('[a-zA-Z- \']*')]],
        lastName: ['', [Validators.required, Validators.pattern('[a-zA-Z- \']*')]],
        email: ['', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]],
        phone: ['', [Validators.required, Validators.pattern('[0-9-]{12}')]],
        businessName: [''],
        verifyCustomerInformation: [null, [Validators.required]],
        passcode: [''],
        confirmPasscode: [''],
      }),
      creditInfoGroup: this.fb.group({
        dob: [''],
        ssnConsent: [null]
      }),
      serviceAddressGroup: this.fb.group({
        useServiceAddress: [false],
        serviceAddress: [''],
        city: [''],
        state: [''],
        zipCode: [''],
        unitType: ['', []],
        unitNumber: ['', []],
        structureType: ['', []],
        structureNumber: ['', []],
        floor: ['', []]
      }),
      ssnGroup: this.fb.group({
        ssn: [''],
        last4Ssn: [''],
        rawSsn: [''],
        noSsnCheckbox: [false]
      }),
      idTypeGroup: this.fb.group({
        idType: [null],
        idName: [null],
      }),
      dlGroup: this.fb.group({
        issuingAuthority: [null, [Validators.required]],
        idNumber: ['', [Validators.required]],
        expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'), expiredDateValidator()]]
      }),
      stateGroup: this.fb.group({
        issuingAuthority: [null, [Validators.required]],
        idNumber: ['', [Validators.required]],
        expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'), expiredDateValidator()]]
      }),
      militaryIdGroup: this.fb.group({
        issuingAuthority: [null, [Validators.required]],
        idNumber: ['', [Validators.required]],
        expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'), expiredDateValidator()]]
      }),
      passportGroup: this.fb.group({
        issuingAuthority: [null, [Validators.required]],
        idNumber: ['', [Validators.required]],
        expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'), expiredDateValidator()]]
      }),
      foreignGovernmentIdGroup: this.fb.group({
        issuingAuthority: [null, [Validators.required]],
        idNumber: ['', [Validators.required]],
        expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'), expiredDateValidator()]]
      })
    });

    this.creditInfoGroup.get('ssnConsent')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.idTypeGroup.get('idType')?.addValidators([Validators.required]);
        this.ssnGroup.get('ssn')?.removeValidators([Validators.required, Validators.pattern('^(\\d{3}-?\\d{2}-?\\d{4}|XXX-XX-XXXX)$')]);
        this.ssnGroup.get('last4Ssn')?.removeValidators([Validators.required, Validators.pattern(/^[0-9]{4}$/)]);
      } else if (value === false) { // Don't change to condition to negation, as null is default value
        this.idTypeGroup.get('idType')?.removeValidators([Validators.required]);
        if (this.fullSsnConditional() || this.fullSsnRequired()) {
          this.ssnGroup?.get('ssn')?.addValidators([Validators.required, Validators.pattern('^(\\d{3}-?\\d{2}-?\\d{4}|XXX-XX-XXXX)$')])
        }
        if ((this.last4SsnConditional() && !this.fullSsnConditional()) || this.last4SsnRequired()) {
          this.ssnGroup?.get('last4Ssn')?.addValidators([Validators.required, Validators.pattern(/^[0-9]{4}$/)])
        }
      }

      this.idTypeGroup.get('idType')?.reset();
      this.ssnGroup.reset();
    });

    this.idTypeGroup.get('idType')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: IdType) => {
      this.selectedIdType = value;
      this.dlGroup.reset();
      this.stateGroup.reset();
      this.militaryIdGroup.reset();
      this.passportGroup.reset();
      this.foreignGovernmentIdGroup.reset();
      this.selectedState = undefined;
      switch (value) {
        case 'Driver’s License':
          this.dlGroup.controls['idNumber'].disable();
          break;
        case 'U.S. Military ID':
          this.militaryIdGroup.get('issuingAuthority')?.setValue('US');
          break;
        case 'U.S. Passport':
          this.passportGroup.get('issuingAuthority')?.setValue('US');
          break;
        case 'Foreign Government ID':
          break;
        case 'State Issued ID':
          break;
        default:
          break;
      }
    });

    this.dlGroup.controls['issuingAuthority']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (dlAuthority) => {
        const stateCode = dlAuthority;
        const idNumberControl = this.dlGroup.get('idNumber')!;
        const idType = this.idTypeGroup.get('idType')!.value
        this.selectedState = Static_States.find(state => state.code === stateCode);

        // Set state-specific validator
        idNumberControl.enable();
        if (this.selectedState) {
          this.selectedState.errorMessage = `Please enter a valid ${this.selectedState.name} ${idType} Number. (${this.selectedState.tooltip})`;
          idNumberControl.setValidators([Validators.required, Validators.pattern(this.selectedState.pattern)
          ]);
          this.currentStateTooltip = this.selectedState.tooltip;
        } else {
          // Fallback validator if state pattern isn't found
          idNumberControl.setValidators([Validators.required, Validators.pattern('^[A-Za-z0-9]{1,15}$')
          ])
          this.currentStateTooltip = 'Enter ID number';
        }
      }
    });

    this.ssnGroup.get('ssn')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        const rawSsn = (value || '').replace(/\D/g, '');
        this.ssnGroup.get('rawSsn')?.setValue(rawSsn, { emitEvent: false });
      });

    this.ssnGroup.get('noSsnCheckbox')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noSsn: boolean) => {
        if (noSsn) {
          this.ssnGroup.get('ssn')?.setValue('');
        }
        this.ssnGroup.get('ssn')?.updateValueAndValidity();
      });


    this.serviceAddressGroup.get('useServiceAddress')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((useServiceAddress: boolean) => {
        this.updateServiceAddressValidators(useServiceAddress);
      });
    // Initialize validators based on the initial value
    this.updateServiceAddressValidators(this.serviceAddressGroup.get('useServiceAddress')?.value || false);

    this.creditInformationForm?.disable();
  }

  private updateServiceAddressValidators(useServiceAddress: boolean): void {
    const serviceAddressControl = this.serviceAddressGroup.get('serviceAddress');
    const cityControl = this.serviceAddressGroup.get('city');
    const stateControl = this.serviceAddressGroup.get('state');
    const zipCodeControl = this.serviceAddressGroup.get('zipCode');

    if (useServiceAddress) {
      // Remove validators and clear errors when using service address
      serviceAddressControl?.clearValidators();
      serviceAddressControl?.setErrors(null);
      cityControl?.clearValidators();
      cityControl?.setErrors(null);
      stateControl?.clearValidators();
      stateControl?.setErrors(null);
      zipCodeControl?.clearValidators();
      zipCodeControl?.setErrors(null);

      // Clear any existing values
      serviceAddressControl?.setValue(null);
      cityControl?.setValue(null);
      stateControl?.setValue(null);
      zipCodeControl?.setValue(null);
    } else {
      // Add required validators when not using service address
      serviceAddressControl?.setValidators([Validators.required]);
      cityControl?.setValidators([Validators.required]);
      stateControl?.setValidators([Validators.required]);
      zipCodeControl?.setValidators([Validators.required, Validators.minLength(5)]);
    }

    // Update validity for all controls
    serviceAddressControl?.updateValueAndValidity();
    cityControl?.updateValueAndValidity();
    stateControl?.updateValueAndValidity();
    zipCodeControl?.updateValueAndValidity();
  }

  filterAddress(event: AutoCompleteCompleteEvent | null, additionalSearch?: string) {
    const enteredAddress = additionalSearch ?? event?.query;

    if (enteredAddress) {
      this.#secureDataService.getSuggestionAddresses(enteredAddress).subscribe({
        next: (resp) => {
          if (resp && resp.suggestions.length > 0) {
            this.addresses = resp
            this.filteredAddresses = this.addresses.suggestions.map((item) => this.mapAddress(item))
          } else {
            this.addresses = { suggestions: [] }
            this.filteredAddresses = []
          }
          if (additionalSearch) {
            this.triggerAutoComplete(resp)
          }
        },
        error: (err) => {
          console.log(err)
          this.addresses = { suggestions: [] }
          this.filteredAddresses = []
          // this.notificationService.showError("Error in getting address suggestions")
        }
      })
    }
  }

  mapAddress(item: Suggestion) {
    let entries = ""
    let unit = ""
    if (item.entries > 1) {
      entries = ` (${item.entries} more entries)`
      unit = ` ${item.secondary}`
    }
    if (item.entries === 1) {
      unit = ` ${item.secondary}`
    }
    return { label: `${item.street_line}${unit}${entries}, ${item.city}, ${item.state} ${item.zipcode}`, value: item }
  }

  validateAddress() {
    const addressFormValue = this.creditInformationForm?.controls['serviceAddressGroup']?.getRawValue();
    if (addressFormValue.zipCode) {
      console.log(addressFormValue);
    }
  }

  onAddressSelect(event: { value: { value: Suggestion } }) {
    const value = event?.value?.value;
    // when entries is selected
    if (value.entries > 1) {
      this.filterAddress(null, this.buildAdditionalAddressSearchParam(value))
      return
    }
    this.clearAddressSection()
    // when apartment or suite is selected
    if (value.entries === 1) {
      this.onAdditionalAddressSelect(value)
    }

    // manually write value to primeNG input mask since it doesn't work well with reactive form patchValue
    this.zipCodeInput?.writeValue(value.zipcode);

    this.selectedAddress = `${value.street_line}, ${value.city}, ${value.state} ${value.zipcode}`
    this.creditInformationForm?.get('serviceAddressGroup')?.patchValue({
      serviceAddress: value.street_line,
      zipCode: value.zipcode,
      city: value.city,
      state: value.state
    })

    this.validateAddress()
  }

  removeStructure() {
    this.displayStructureType = false
    this.serviceAddressGroup?.patchValue({
      structureType: null,
      structureNumber: null
    })
    this.removeFloor()
  }

  removeFloor() {
    this.displayFloor = false
    this.serviceAddressGroup?.patchValue({
      floor: null
    })
  }

  onAdditionalAddressSelect(value: Suggestion) {
    this.serviceAddressGroup?.get('unitType')?.addValidators(Validators.required)
    this.serviceAddressGroup?.get('unitNumber')?.addValidators(Validators.required)
    this.serviceAddressGroup?.patchValue({
      unitType: value.secondary.split(' ')[0],
      unitNumber: value.secondary.split(' ')[1],
    })
    if (!this.showFullAddress) {
      this.showFullAddressBtn.el.nativeElement.firstChild.click()
    }
  }

  triggerAutoComplete(resp: AddressSuggestions) {
    this.serviceAddressGroup?.get('serviceAddress')?.setValue(
      resp.suggestions[0]?.street_line + ' ' + resp.suggestions[0]?.secondary?.split(' ')[0]
    )
    setTimeout(() => {
      this.autoComplete?.show()
    }, 200)
  }

  buildAdditionalAddressSearchParam(value: Suggestion): string {
    return encodeURIComponent(value.street_line?.replaceAll('%20',' ')) 
    + '&selected=' 
    + encodeURIComponent(`${value.street_line} ${value.secondary} (${value.entries}) ${value.city} ${value.state} ${value.zipcode}`.replaceAll('%20',' '))
  }

  clearAddressSection() {
    this.serviceAddressGroup?.get('unitType')?.setErrors(null)
    this.serviceAddressGroup?.get('unitNumber')?.setErrors(null)
    this.serviceAddressGroup?.get('unitType')?.removeValidators(Validators.required)
    this.serviceAddressGroup?.get('unitNumber')?.removeValidators(Validators.required)
    this.serviceAddressGroup?.updateValueAndValidity()
    this.serviceAddressGroup?.patchValue({
      serviceAddress: null,
      city: null,
      state: null,
      zipCode: null,
      unitType: null,
      unitNumber: null,
      structureType: null,
      structureNumber: null,
      floor: null
    })
  }

  // Optional SSN still needs an answer: a valid SSN/ITIN or "No SSN provided" checked.
  private readonly ssnOrNoSsnValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    control.value || control.parent?.get('noSsnCheckbox')?.value ? null : { required: true };
  private readonly optionalSsnPatternValidator: ValidatorFn = Validators.pattern('^(\\d{3}-?\\d{2}-?\\d{4}|XXX-XX-XXXX)$');

  initConditionalValidators() {

    if (this.creditOnFile()) {
      ['ssn', 'last4Ssn'].forEach(name => {
        this.ssnGroup?.get(name)?.clearValidators()
        this.ssnGroup?.get(name)?.updateValueAndValidity()
      })
      this.creditInfoGroup?.get('dob')?.clearValidators()
      this.creditInfoGroup?.get('dob')?.updateValueAndValidity()
    } else if (this.fullSsnConditional() || this.fullSsnRequired()) {
      this.ssnGroup?.get('ssn')?.addValidators([Validators.required, Validators.pattern('^(\\d{3}-?\\d{2}-?\\d{4}|XXX-XX-XXXX)$')])
    } else if (this.fullSsnOptional()) {
      this.ssnGroup?.get('ssn')?.addValidators([this.ssnOrNoSsnValidator, this.optionalSsnPatternValidator])
    }

    if (!this.creditOnFile() && ((this.last4SsnConditional() && !this.fullSsnConditional()) || this.last4SsnRequired())) {
      this.ssnGroup?.get('last4Ssn')?.addValidators([Validators.required, Validators.pattern(/^[0-9]{4}$/)])
    }

    if (this.dobRequired() && !this.creditOnFile()) {
      this.creditInfoGroup?.get('dob')?.addValidators([
        Validators.required,
        Validators.pattern('^(0[1-9]|1[012])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](19|20)\\d\\d$'),
        futureDateValidator(),
        minimumAgeValidator(18)
      ])
    }

    if (this.photoIdRequired()) {
      this.idTypeGroup?.get('idType')?.addValidators([Validators.required])
    }

    if (this.passcodeRequired()) {
      this.customerInfoGroup.get('passcode')?.setValidators([Validators.required, Validators.pattern(/^\d{6,8}$/), this.passwordMatchValidator]);
      this.customerInfoGroup.get('confirmPasscode')?.setValidators([Validators.required, Validators.pattern(/^\d{6,8}$/), this.passwordMatchValidator]);

      this.customerInfoGroup.get('passcode')?.updateValueAndValidity()
      this.customerInfoGroup.get('confirmPasscode')?.updateValueAndValidity()
    } else {
      this.customerInfoGroup.get('passcode')?.clearValidators()
      this.customerInfoGroup.get('confirmPasscode')?.clearValidators()
      this.customerInfoGroup.get('passcode')?.updateValueAndValidity()
      this.customerInfoGroup.get('confirmPasscode')?.updateValueAndValidity()
    }
  }

  getPasscodeErrorMessage(control: AbstractControl): string {
    if (control.hasError('required')) {
      return 'Passcode is required'
    }
    if (control.hasError('pattern')) {
      return 'Passcode must be between 6 to 8 digits'
    }
    return '';
  }

  onKeyPressAllowOnlyDigits(event: KeyboardEvent): boolean {
    // Allow only digits (0-9) and control keys like backspace, delete, arrows
    const isDigit = /^\d$/.test(event.key)
    const isControlKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)

    return isDigit || isControlKey
  }

  onApiEmailValidation($event: EmailVerifyStatus) {
    if ($event === EmailVerifyStatus.VerifiedByCustomer) {
      this.isEmailVerifiedByCustomer.set(true);
    } else {
      this.isEmailVerifiedByCustomer.set(false);
    }
  }

  onApiPhoneValidation($event: PhoneVerifyStatus) {
    if ($event === PhoneVerifyStatus.VerifiedByCustomer) {
      this.isPhoneVerifiedByCustomer.set(true);
    } else {
      this.isPhoneVerifiedByCustomer.set(false);
    }
  }

  passwordMatchValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) return null;

    const passcodeControl = control.parent.get('passcode');
    const confirmPasscodeControl = control.parent.get('confirmPasscode');
    const passcode = passcodeControl?.value;
    const confirmPasscode = confirmPasscodeControl?.value;

    // Only validate if both fields have values
    if (!passcode || !confirmPasscode) return null;

    if (passcode !== confirmPasscode) {
      return { passwordMismatch: true };
    } else {
      // Clear passwordMismatch error from both controls if they match
      [passcodeControl, confirmPasscodeControl].forEach(ctrl => {
        if (ctrl?.hasError('passwordMismatch')) {
          const currentErrors = ctrl.errors;
          if (currentErrors) {
            // Create new errors object without passwordMismatch
            const remainingErrors = this.omit(currentErrors, ['passwordMismatch']);

            // If no other errors, set to null, otherwise set the remaining errors
            const hasOtherErrors = Object.keys(remainingErrors).length > 0;
            ctrl.setErrors(hasOtherErrors ? remainingErrors : null);
          }
        }
      });
    }

    return null;
  };

  omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (!keys.includes(key as K)) {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as Omit<T, K>);
  }

  get customerInfoGroup() {
    return this.creditInformationForm?.controls['customerInfoGroup'] as FormGroup;
  }

  get serviceAddressGroup() {
    return this.creditInformationForm?.controls['serviceAddressGroup'] as FormGroup;
  }

  get creditInfoGroup() {
    return this.creditInformationForm?.controls['creditInfoGroup'] as FormGroup;
  }

  get ssnGroup() {
    return this.creditInformationForm?.controls['ssnGroup'] as FormGroup;
  }

  get idTypeGroup() {
    return this.creditInformationForm?.controls['idTypeGroup'] as FormGroup;
  }

  get dlGroup() {
    return this.creditInformationForm?.controls['dlGroup'] as FormGroup;
  }

  get stateGroup() {
    return this.creditInformationForm?.controls['stateGroup'] as FormGroup;
  }

  get militaryIdGroup() {
    return this.creditInformationForm?.controls['militaryIdGroup'] as FormGroup;
  }

  get passportGroup() {
    return this.creditInformationForm?.controls['passportGroup'] as FormGroup;
  }

  get foreignGovernmentIdGroup() {
    return this.creditInformationForm?.controls['foreignGovernmentIdGroup'] as FormGroup;
  }

  get ssnConsent() {
    return this.creditInfoGroup?.controls['ssnConsent'] as FormControl;
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'CreditInfo' && val.Data != undefined) {
      console.log('rawData', val);
      const data = val.Data;
      this.creditInformationState.set(val.Data);
      if ((data as { orderType?: string })['orderType'] === 'smbnew') {
        this.isSMB = true;
        this.customerInfoGroup.get('businessName')?.setValidators([Validators.required, Validators.maxLength(200)]);
        this.customerInfoGroup.get('businessName')?.updateValueAndValidity({ emitEvent: false });
      }
      const address = data['serviceAddress'];
      const unitType = data['unitType'];
      const unitNumber = data['unitNumber'];
      let unitPart = '';
      if (unitType && unitNumber) {
        unitPart = `${unitType} ${unitNumber}`;
      }
      const city = data['city'];
      const state = data['state']
      const zipCode = (data['zipCode'] as string | undefined)?.indexOf('-') > -1 ? data['zipCode'].split('-')[0] : data['zipCode'];
      const foormattedAddress = [address, unitPart, city, state, zipCode].filter(Boolean).join(', ');
      this.serviceAddress = foormattedAddress;
      console.log('rawData', val);
      console.log('rawData', val);
      if (data && typeof data == 'object') {
        this.patchData(data);
        this.creditOnFile.set(!!data['creditOnFile']);
        this.creditSetup.set(data['creditSetup']);
        if (!this.creditCheckRequired()) {
          this.isConsentAgreed = true;
          this.onConsentChecked(true);
        }
        if (!this.allowBillingAddressChange()) {
          this.serviceAddressGroup.get('useServiceAddress')?.setValue(true);
        }
        if (!this.showBillingAddressField()) {
          this.serviceAddressGroup.patchValue(data['billingAddress']);
        }
        this.authUserList.set(data['authUserList']);
        this.creditChecked.set(data['creditChecked'])
        if (this.authUserList()?.length > 0) {
          this.idTypeGroup.get('idName')?.addValidators([Validators.required])
          if (this.authUserList().length === 1) {
            this.idTypeGroup.get('idName')?.setValue(this.authUserList()[0]);
            this.idTypeGroup.get('idName')?.disable();
          }
        }
        this.creditInformationForm?.updateValueAndValidity();
        this.legalDescription = this.formattedInternetCreditCheckConsentVerbiage(data['legalDescription']);
      }

    }
  }

  formattedInternetCreditCheckConsentVerbiage(text) {
    if (!text) return text;
    return text
      .split('\\n\\n')  // split on \n\n
      .map(paragraph => `<span>${paragraph.trim()}</span>`)
      .join('')
  }

  onConsentChecked(event: boolean) {
    if (!event) {
      this.creditInformationForm?.disable();
    } else {
      this.creditInformationForm?.enable({ emitEvent: false });
      // If there's exactly one authorized user, disable the idName control
      if (this.authUserList()?.length === 1) {
        this.idTypeGroup.get('idName')?.disable();
      }
      this.triggerFormatPhoneNumberAndInputBlur();
    }
  }

  submitClicked() {
    if (this.emailValidationRequired() && !this.isEmailVerifiedByCustomer()) {
      this.notificationService.showError('Please verify your email address.')
      return
    }

    if (this.phoneValidationRequired() && !this.isPhoneVerifiedByCustomer()) {
      this.notificationService.showError('Please verify your phone number.')
      return
    }
    if (!this.ssnGroup.valid || !this.creditInfoGroup.valid) {
      this.setSsnData(this.creditInformationState())
    }

    console.log(this.creditInfoGroup.errors);
    const isCustomerValid = this.customerInfoGroup.valid;
    const isCreditInfoValid = this.creditInfoGroup.valid;
    const isServiceAddressValid = this.serviceAddressGroup.valid;
    const isSSNValid = this.ssnGroup.valid;
    let isIdTypeValid = false;
    let isIdTypeSelectedValid = false;
    isIdTypeValid = this.idTypeGroup.valid;
    const selectedIdType: IdType = this.idTypeGroup.getRawValue().idType;
    const shouldValidateSelectedId = (this.photoIdRequired() || this.ssnConsent?.value || !!selectedIdType) && this.showPhotoIdField();

    switch (selectedIdType) {
      case 'Driver’s License':
        isIdTypeSelectedValid = this.dlGroup.valid;
        break;
      case 'Foreign Government ID':
        isIdTypeSelectedValid = this.foreignGovernmentIdGroup.valid;
        break;
      case 'State Issued ID':
        isIdTypeSelectedValid = this.stateGroup.valid;
        break;
      case 'U.S. Military ID':
        isIdTypeSelectedValid = this.militaryIdGroup.valid;
        break;
      case 'U.S. Passport':
        isIdTypeSelectedValid = this.passportGroup.valid;
        break;
      default:
        isIdTypeSelectedValid = false;
        break;
    }

    if (shouldValidateSelectedId && !isIdTypeSelectedValid) {
      this.creditInformationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;

    }

    

    if ( isCustomerValid && isCreditInfoValid && isServiceAddressValid && isSSNValid && isIdTypeValid &&(!shouldValidateSelectedId || isIdTypeSelectedValid)) {
      
      const data = this.mapValueFromToData();
      this.#secureDataService.submitCustomerData(data, 'CreditInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.creditInformationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  onCancelClick() {
    this.showCancelDialog = true;
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('CreditInfo').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateWaiting(true);
          },
          error: (err) => this.onCancelError('CreditInfo', err)
        })
    }
    // console.log(data);
  }

  triggerFormatPhoneNumberAndInputBlur() {
    if (this.phoneVerificationComponent) {
      this.phoneVerificationComponent.onInputBlur()
    }
    if (this.emailVerificationComponent) {
      this.emailVerificationComponent.onInputBlur()
    }
  }

  patchData(data) {
    if (data) {
      this.customerInfoGroup.patchValue(data);
      this.customerInfoGroup.controls['businessName'].setValue(data?.businessName ?? data?.customerInformationQualityCheck?.businessName ?? data?.businessNameSMB ?? '');
      this.customerInfoGroup.controls['verifyCustomerInformation'].setValue(data?.customerInformationQualityCheck?.verifyCustomerInformation);
      this.customerInfoGroup.controls['verifyCustomerInformation'].setValue(data?.customerInformationQualityCheck?.verifyCustomerInformation);
      this.creditInfoGroup.patchValue(data);
      // this.serviceAddressGroup.patchValue(data);
      const creditCheckData = data?.customerInformationCreditCheck ?? data;
      const portalIdTypeLabels: Record<string, IdType> = {
        drivers_license: 'Driver’s License',
        state_issued_id: 'State Issued ID',
        us_military_id: 'U.S. Military ID',
        us_passport: 'U.S. Passport',
        foreign_government_id: 'Foreign Government ID'
      };
      const idTypeLabel = this.idTypes.includes(creditCheckData.idType)
        ? creditCheckData.idType
        : portalIdTypeLabels[creditCheckData.idType] ?? null;
      const savedIdType = creditCheckData.idNumber ? idTypeLabel : null;
      this.ssnGroup.patchValue(creditCheckData);
      this.idTypeGroup.patchValue({ ...creditCheckData, idType: savedIdType });


      //DOB Mapping
      this.setDobData(creditCheckData);

      this.setSsnData(creditCheckData)
      this.idTypeGroup.get('idType')?.setValue(savedIdType, { emitEvent: false });
      this.selectedIdType = savedIdType;
      if (data.idType) {
        this.patchIdTypeData(data);
      } else if (idTypeLabel) {
        this.patchIdTypeData({ ...creditCheckData, idType: idTypeLabel });
      }

    }

  }

  private patchIdTypeData(data: any) {
    let expiration = '';
    if (data.expirationDay && data.expirationMonth && data.expirationYear) {
      expiration = `${String(data.expirationMonth).padStart(2, '0')}/${String(data.expirationDay).padStart(2, '0')}/${data.expirationYear}`;
    }

    switch (data.idType) {
      case 'Driver’s License':
        this.dlGroup.patchValue(data);
        this.dlGroup.controls['expirationDate'].setValue(expiration);
        break;
      case 'Foreign Government ID':
        this.foreignGovernmentIdGroup.patchValue(data);
        this.foreignGovernmentIdGroup.controls['expirationDate'].setValue(expiration);
        break;
      case 'State Issued ID':
        this.stateGroup.patchValue(data);
        this.stateGroup.controls['expirationDate'].setValue(expiration);
        break;
      case 'U.S. Military ID':
        this.militaryIdGroup.patchValue(data);
        this.militaryIdGroup.controls['expirationDate'].setValue(expiration);
        break;
      case 'U.S. Passport':
        this.passportGroup.patchValue(data);
        this.passportGroup.controls['expirationDate'].setValue(expiration);
        break;
    }
  }

  mapValueFromToData(): CreditInfoResponse {
    let data = Object.assign({},
      this.customerInfoGroup.getRawValue(),
      this.creditInfoGroup.getRawValue(),
      this.serviceAddressGroup.getRawValue(),
      this.ssnGroup.getRawValue()
    );

    if (this.serviceAddressGroup.getRawValue().useServiceAddress) {
      // When using service address, use the existing serviceAddress value
      data.useServiceAddress = true;
    } else {
      // When not using service address, use the form values
      data = Object.assign(data, this.serviceAddressGroup.getRawValue());
    }

    if (data.dob) {
      const dateModel = this.getDateModel(data.dob);
      if (dateModel) {
        data.dobMonth = dateModel.month;
        data.dobDay = dateModel.day;
        data.dobYear = dateModel.year;
      }
    }

    if (this.creditOnFile()) {
      // Stored values are used for the credit check; never send new ones.
      data.ssn = '';
      data.last4Ssn = '';
      data.dob = '';
      data.dobMonth = '';
      data.dobDay = '';
      data.dobYear = '';
    } else if ((this.fullSsnOptional() || this.fullSsnConditional()) && !data.ssn) {
      data.ssn = GENERIC_SSN;
    }

    data = Object.assign(data, this.idTypeGroup.getRawValue());

    if (this.idTypeGroup.getRawValue()) {
      const idType: IdType = this.idTypeGroup.getRawValue().idType;
      switch (idType) {
        case 'Driver’s License':
          data = Object.assign(data, this.dlGroup.getRawValue());
          this.setIdExpiration(data, this.dlGroup.getRawValue().expirationDate);
          break;
        case 'Foreign Government ID':
          data = Object.assign(data, this.foreignGovernmentIdGroup.getRawValue());
          this.setIdExpiration(data, this.foreignGovernmentIdGroup.getRawValue().expirationDate);
          break;
        case 'State Issued ID':
          data = Object.assign(data, this.stateGroup.getRawValue());
          this.setIdExpiration(data, this.stateGroup.getRawValue().expirationDate);
          break;
        case 'U.S. Military ID':
          data = Object.assign(data, this.militaryIdGroup.getRawValue());
          this.setIdExpiration(data, this.militaryIdGroup.getRawValue().expirationDate);
          break;
        case 'U.S. Passport':
          data = Object.assign(data, this.passportGroup.getRawValue());
          this.setIdExpiration(data, this.passportGroup.getRawValue().expirationDate);
          break;
        default:
          break;
      }
    }
    // console.log(data);
    return data;
  }

  setIdExpiration(data: CreditInfoResponse, expiration: string) {
    const dateModel = this.getDateModel(expiration);
    if (dateModel) {
      data.expirationMonth = dateModel.month;
      data.expirationDay = dateModel.day;
      data.expirationYear = dateModel.year;
    }
  }

  getDateModel(date: string) {
    const result: { month: string, day: string, year: string } =
    {
      day: '',
      month: '',
      year: ''
    };
    if (date) {
      const dobDate = new Date(date);
      result.month = (dobDate.getMonth() + 1).toString();
      result.day = dobDate.getDate().toString();
      result.year = dobDate.getFullYear().toString();
    }

    return result;
  }

  setSsnData(data) {
    const creditCheck = data?.customerInformationCreditCheck;
    const ssnConsent = data?.customerInformationCreditCheck?.ssnConsent;

    if (creditCheck) {
      const ssnValue = creditCheck?.rawSsn || creditCheck?.ssn;

      if (ssnValue) {
        const last4Ssn = creditCheck?.last4Ssn
          ? creditCheck.last4Ssn
          : ssnValue.slice(-4);

        this.ssnGroup.controls['ssn'].setValue(ssnValue);
        this.ssnGroup.controls['last4Ssn'].setValue(last4Ssn);
        // this.creditInfoGroup.controls['ssnConsent'].setValue(true);
      }
    }

    if (ssnConsent) {
      this.creditInfoGroup.controls['ssnConsent'].setValue(ssnConsent);
    }
  }

  setDobData(data) {
    let dob = '';

    const formatDob = (month: string | number, day: string | number, year: string | number) => {
      const mm = String(month).padStart(2, '0');
      const dd = String(day).padStart(2, '0');
      return `${mm}/${dd}/${year}`;
    };

    if (data?.dobDay && data?.dobMonth && data?.dobYear) {
      dob = formatDob(data.dobMonth, data.dobDay, data.dobYear);
    } else if (
      data?.customerInformationCreditCheck?.dobDay &&
      data?.customerInformationCreditCheck?.dobMonth &&
      data?.customerInformationCreditCheck?.dobYear
    ) {
      const creditCheck = data.customerInformationCreditCheck;
      dob = formatDob(creditCheck.dobMonth, creditCheck.dobDay, creditCheck.dobYear);
    }

    if (dob) {
      dob = this.#dateService.maskDate(dob);
      this.creditInfoGroup.controls['dob'].setValue(dob);
    }
  }




}

export function minimumAgeValidator(minimumAge: number = 18): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.valid && control.value) {
      const currentDate = new Date();
      const [month, day, year] = control.value.split('/').map(Number);
      const inputDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor

      // Calculate age
      let age = currentDate.getFullYear() - inputDate.getFullYear();
      const monthDiff = currentDate.getMonth() - inputDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < inputDate.getDate())) {
        age--;
      }

      if (age >= minimumAge) {
        return null; // Valid
      } else {
        return { 'minimumAge': { value: control.value, requiredAge: minimumAge, actualAge: age } };
      }
    }

    return null; // Valid
  };
}

export function futureDateValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {

    if (control.valid && control.value) {
      const currentDate = new Date();
      const [month, day, year] = control.value.split('/').map(Number);
      const inputDate = new Date(year, month, day);

      if (inputDate < currentDate) {
        return null; // Valid
      } else {
        return { 'futureDate': { value: control.value } }; // Invalid - not a future date
      }
    }

    return null; // Valid
  };
}

export function expiredDateValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {


    if (control.valid && control.value) {
      const currentDate = new Date();
      const [month, day, year] = control.value.split('/').map(Number);
      const inputDate = new Date(year, month, day);

      if (inputDate > currentDate) {
        return null; // Valid
      } else {
        return { 'expiredDate': { value: control.value } }; // Invalid - not a future date
      }
    }
    return null; // Valid
  };
}
export interface CreditInfoResponse {
  customerId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string
  ssn: string;
  ssnConsent: boolean
  idType: IdType;
  idNumber: string;
  issuingAuthority: string;
  expirationMonth: string;
  expirationDay: string;
  expirationYear: string;
  verifyCustomerInformation: boolean;
  serviceAddress: string;
  zipCode: string;
  city: string;
  state: string;
  unitType: string;
  unitNumber: string;
  structureType: string;
  structureNumber: string;
  floor: string;
}
